/**
 * AddFieldModal — F12.D T12.D.9.
 *
 * Modal „Dodaj nowe pole" w Ustawieniach → Pola modułów.
 * Pokazuje 15 curated typów pól + dropdown wyboru modułu.
 *
 * Backend: POST `/api/modules/:moduleId/fields` (już istnieje od F12.A).
 *
 * NEG (deferred):
 *  - 33 typy zaawansowane (signature, qrcode, formula, lookup, rollup...) — pod toggle
 *    „Pokaż zaawansowane" do dorobienia w osobnym tasku.
 *  - Konfiguracja relacji (relationModuleId, displayField) — robi się w buildery po dodaniu.
 *  - Konfiguracja opcji select — robi się w buildery po dodaniu (osobny CRUD).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Dropdown } from '@/components/Dropdown';
import { CURATED_FIELD_TYPES, fieldTypeI18nKey } from '@/components/fields/curated-types';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Textarea } from '@/components/Textarea';
import { useToast } from '@/contexts/ToastContext';
import { reservedFieldSlugRenameDescription } from '@/hooks/useReservedFieldSlugToast';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';

interface ModuleOption {
  id: string;
  name: string;
}

interface AddFieldModalProps {
  open: boolean;
  onClose: () => void;
  modules: ModuleOption[];
  /** Opcjonalny pre-selected moduł (gdy user kliknął „Dodaj pole" przy konkretnym module). */
  defaultModuleId?: string;
}

interface FormState {
  moduleId: string;
  name: string;
  fieldType: string;
  description: string;
  isRequired: boolean;
  isUnique: boolean;
  isSearchable: boolean;
  isFilterable: boolean;
  showInList: boolean;
}

const INITIAL_FORM: FormState = {
  moduleId: '',
  name: '',
  fieldType: 'text',
  description: '',
  isRequired: false,
  isUnique: false,
  isSearchable: true,
  isFilterable: false,
  showInList: true,
};

export function AddFieldModal({ open, onClose, modules, defaultModuleId }: AddFieldModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);

  // Reset state on open + ustaw default module
  useEffect(() => {
    if (open) {
      setForm({
        ...INITIAL_FORM,
        moduleId: defaultModuleId ?? modules[0]?.id ?? '',
      });
    }
  }, [open, defaultModuleId, modules]);

  const createMutation = useMutation({
    mutationFn: async (input: FormState) => {
      const res = await api.post<{ field: { id: string; slug: string } }>(
        `/modules/${input.moduleId}/fields`,
        {
          name: input.name.trim(),
          fieldType: input.fieldType,
          description: input.description.trim() === '' ? null : input.description.trim(),
          isRequired: input.isRequired,
          isUnique: input.isUnique,
          isSearchable: input.isSearchable,
          isFilterable: input.isFilterable,
          showInList: input.showInList,
        },
      );
      return res.data.field;
    },
    onSuccess: async (_field, variables) => {
      toast.show({ variant: 'success', title: t('tenant.moduleFields.toasts.added') });
      // refetchQueries (zamiast invalidateQueries) wymusza natychmiastowy fetch i zwraca
      // promise który czekamy — dzięki temu lista pól pokazuje nowe pole zanim zamkniemy modal.
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['modules'] }),
        queryClient.refetchQueries({ queryKey: ['module-fields'] }),
        queryClient.refetchQueries({ queryKey: ['module-fields', variables.moduleId] }),
        queryClient.refetchQueries({ queryKey: ['module-records'] }),
        queryClient.invalidateQueries({ queryKey: ['module-by-slug'] }),
      ]);
      onClose();
    },
    onError: (err) => {
      toast.show({
        variant: 'error',
        title: t('tenant.moduleFields.toasts.addError'),
        // Nazwa techniczna zarezerwowana przez system (np. „decisions”) — czytelny opis zamiast kodu;
        // błąd bez kodu (np. brak sieci) — bez opisu, sam tytuł.
        description: reservedFieldSlugRenameDescription(err, t) ?? extractErrorCode(err),
      });
    },
  });

  const handleSubmit = () => {
    if (!form.moduleId || !form.name.trim()) return;
    createMutation.mutate(form);
  };

  const canSubmit = !!form.moduleId && form.name.trim().length > 0 && !createMutation.isPending;

  return (
    <Modal open={open} onClose={onClose} title={t('tenant.moduleFields.modal.title')} size="md">
      <div className="space-y-4">
        <FormField label={t('tenant.moduleFields.modal.moduleLabel')} required>
          <SearchableSelect
            value={form.moduleId || null}
            onChange={(val) => setForm({ ...form, moduleId: val ?? '' })}
            placeholder={t('tenant.moduleFields.modal.modulePlaceholder')}
            options={modules.map((m) => ({ value: m.id, label: m.name }))}
            aria-label={t('tenant.moduleFields.modal.moduleLabel')}
            data-testid="add-field-module-select"
          />
        </FormField>

        <FormField label={t('tenant.moduleFields.modal.fieldName')} required>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={t('tenant.moduleFields.modal.fieldNamePlaceholder')}
            maxLength={100}
            autoFocus
            data-testid="add-field-name-input"
          />
        </FormField>

        <FormField label={t('tenant.moduleFields.modal.fieldType')} required>
          <Dropdown
            value={form.fieldType}
            onChange={(val) => setForm({ ...form, fieldType: val })}
            options={CURATED_FIELD_TYPES.map((type) => ({
              value: type,
              label: t(fieldTypeI18nKey(type)),
            }))}
            aria-label={t('tenant.moduleFields.modal.fieldType')}
            data-testid="add-field-type-select"
          />
        </FormField>

        <FormField label={t('tenant.moduleFields.modal.description')}>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder={t('tenant.moduleFields.modal.descriptionPlaceholder')}
            rows={2}
            maxLength={500}
            data-testid="add-field-description"
          />
        </FormField>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <Checkbox
            label={t('tenant.moduleFields.modal.required')}
            checked={form.isRequired}
            onChange={(e) => setForm({ ...form, isRequired: e.target.checked })}
            data-testid="add-field-required"
          />
          <Checkbox
            label={t('tenant.moduleFields.modal.unique')}
            checked={form.isUnique}
            onChange={(e) => setForm({ ...form, isUnique: e.target.checked })}
            data-testid="add-field-unique"
          />
          <Checkbox
            label={t('tenant.moduleFields.modal.searchable')}
            checked={form.isSearchable}
            onChange={(e) => setForm({ ...form, isSearchable: e.target.checked })}
            data-testid="add-field-searchable"
          />
          <Checkbox
            label={t('tenant.moduleFields.modal.filterable')}
            checked={form.isFilterable}
            onChange={(e) => setForm({ ...form, isFilterable: e.target.checked })}
            data-testid="add-field-filterable"
          />
          <Checkbox
            label={t('tenant.moduleFields.modal.showInList')}
            checked={form.showInList}
            onChange={(e) => setForm({ ...form, showInList: e.target.checked })}
            data-testid="add-field-show-in-list"
          />
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={createMutation.isPending}
          disabled={!canSubmit}
          data-testid="add-field-submit"
        >
          {t('tenant.moduleFields.modal.submit')}
        </Button>
      </div>
    </Modal>
  );
}
